import React, { useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import LandingPage from './LandingPage';
import BotsLandingPage from './BotsLandingPage';
import CorePageContent from '@/components/CorePageContent';
// import MarketplaceLanding from './MarketplaceLanding';
import AppShell from '@/components/AppShell';
import { getDefaultAuthenticatedDestination, isBotsLandingHost } from '@/utils/domainDetection';

/**
 * Index - Root page with conditional rendering based on authentication status and user type
 * 
 * Shows:
 * - BotsLandingPage on bots.vaiu.app (MCP / registration docs; no dashboard hijack)
 * - LandingPage for unauthenticated users on other hosts
 * - Hostname default (dashboard on vaiu.app, marketplace on atomicMarketplace.com) for guest users
 * - CorePageContent (dashboard) for authenticated users
 * - Loading spinner while checking auth status
 * 
 * Note: Merchants access merchant tools via sell.vaiu.app explicitly for onboarding
 */
const Index: React.FC = () => {
  const { user, loading, isGuest } = useAuth();
  const navigate = useNavigate();

  const botsHost = isBotsLandingHost();

  useEffect(() => {
    if (botsHost) return;
    if (!loading && user) {
      if (isGuest) {
        navigate(getDefaultAuthenticatedDestination());
        return;
      }
      // All authenticated users (including merchants) see the dashboard (CorePageContent)
      // Merchants access merchant tools via sell.vaiu.app explicitly
    }
  }, [botsHost, user, loading, isGuest, navigate]);

  if (botsHost) {
    return <BotsLandingPage />;
  }

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show appropriate view for authenticated users
  if (user) {
    // Use AppShell with CorePageContent for consistent experience
    return (
      <AppShell>
        <CorePageContent />
      </AppShell>
    );
  }

  // Show landing page for unauthenticated users
  return <LandingPage />;
};

export default Index;
